/**
 * SnapshotPreviewFooter — action buttons for snapshot
 *
 * Provides: delete and restore buttons
 */

import { Trash2, RotateCcw } from "lucide-react";
import { useState } from "react";
import type { SnapshotMeta } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { useHistoryStore } from "../../store/history-store";
import { useEditorStore } from "../../store/editor-store";
import { cn } from "../../lib/utils";

export interface SnapshotPreviewFooterProps {
    snapshot: SnapshotMeta;
    snapshotId: string;
    filePath: string;
    onSnapshotDeleted: () => void;
    getCurrentContent: () => Promise<string>;
    onRestoreSuccess?: (preSnapshotId?: string) => void;
}

export function SnapshotPreviewFooter({
    snapshot,
    snapshotId,
    filePath,
    onSnapshotDeleted,
    getCurrentContent,
    onRestoreSuccess,
}: SnapshotPreviewFooterProps) {
    const t = useT("editor");
    const [deleting, setDeleting] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
    const historyStore = useHistoryStore();
    const setStatusMessage = useEditorStore((s) => s.setStatusMessage);

    // Delete snapshot with custom confirmation
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const success = await historyStore.remove(filePath, snapshotId);
            if (success) {
                setStatusMessage(t("snapshot.status.deleted"));
                onSnapshotDeleted();
            } else {
                setStatusMessage(t("snapshot.status.deleteFailed"));
            }
        } catch {
            setStatusMessage(t("snapshot.status.deleteFailed"));
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // Restore from snapshot
    const handleRestore = async () => {
        setRestoring(true);
        try {
            // Get current content from editor
            const currentContent = await getCurrentContent();

            const result = await historyStore.restore({
                filePath,
                snapshotId,
                currentContent,
            });

            if (result) {
                // Success - trigger callback with preSnapshot ID if available
                if (result.preSnapshot) {
                    setStatusMessage(t("snapshot.status.restoredWithPreSnapshot"));
                    onRestoreSuccess?.(result.preSnapshot.id);
                } else {
                    setStatusMessage(t("snapshot.status.restored"));
                    onRestoreSuccess?.();
                }
            }
        } finally {
            setRestoring(false);
        }
    };

    // Restore confirmation dialog
    if (showRestoreConfirm) {
        return (
            <div className="flex flex-col gap-2 px-3 py-2 border-t border-border bg-muted/10 shrink-0">
                <div className="text-sm font-medium text-foreground">{t("snapshot.confirmRestoreTitle")}</div>
                <div className="text-xs text-muted-foreground mb-2">
                    {t("snapshot.confirmRestoreMessage")}
                </div>
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => setShowRestoreConfirm(false)}
                        disabled={restoring}
                        className="px-3 py-1.5 text-sm rounded border border-border bg-background hover:bg-muted/50 transition-colors disabled:opacity-50"
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleRestore}
                        disabled={restoring}
                        className="px-3 py-1.5 text-sm rounded bg-blue-500/20 text-blue-700 dark:text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                    >
                        {restoring ? t("snapshot.restoring") : t("snapshot.confirmRestore")}
                    </button>
                </div>
            </div>
        );
    }

    // Custom delete confirmation dialog
    if (showDeleteConfirm) {
        return (
            <div className="flex flex-col gap-2 px-3 py-2 border-t border-border bg-muted/10 shrink-0">
                <div className="text-sm text-foreground">{t("snapshot.confirmDeleteMessage")}</div>
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={deleting}
                        className="px-3 py-1.5 text-sm rounded border border-border bg-background hover:bg-muted/50 transition-colors disabled:opacity-50"
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleting}
                        className="px-3 py-1.5 text-sm rounded bg-red-500/20 text-red-700 dark:text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                    >
                        {deleting ? t("snapshot.deleting") : t("snapshot.confirmDelete")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/10 shrink-0 gap-2">
            {/* Left: action buttons */}
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => setShowRestoreConfirm(true)}
                    disabled={restoring}
                    className={cn(
                        "p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-xs font-medium flex items-center gap-1",
                        restoring && "opacity-50 cursor-not-allowed",
                    )}
                    title={t("snapshot.restore")}
                >
                    <RotateCcw size={14} />
                    {t("snapshot.restore")}
                </button>
                <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={deleting}
                    className={cn(
                        "p-1.5 rounded text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors text-xs font-medium flex items-center gap-1",
                        deleting && "opacity-50 cursor-not-allowed",
                    )}
                    title={t("snapshot.delete")}
                >
                    <Trash2 size={14} />
                    {deleting ? t("snapshot.deleting") : t("snapshot.delete")}
                </button>
            </div>

            {/* Right: metadata summary */}
            <div className="text-[10px] text-muted-foreground/60">
                {snapshot.sizeBytes ? (
                    <span>{(snapshot.sizeBytes / 1024).toFixed(1)} KB</span>
                ) : null}
            </div>
        </div>
    );
}
